import { Autocomplete, Box, Button, Divider, IconButton, TextField, Typography } from '@mui/material';
import { DocumentActiveIcon, FileIcon, FolderIcon, MenuVerticalIcon, PlusIcon, UploadFileIcon } from '~/assets/icons';
import styles from './File.module.scss';
import classNames from 'classnames/bind';
import { BaseHeaderTitle, BaseImage, BaseRow } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { OptionUploadFile } from '~/utils/types/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import MenuOptionFolder from '~/components/jobs/menu-option/MenuOption';
import { handleCreateJobFiles, handleGetJobFolders } from '~/thunks/job/jobThunk';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { IJob, IJobFile, IJobFileCreate, IResponseGetFile } from '~/utils/interface/job';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { useFormik } from 'formik';
import BasePagination from '~/components/jobs/base-pagination/BasePagination';
import { jobActions } from '~/thunks/job/jobSlice';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import ModalAlertDeleteFolder from '~/components/common/modal-alert-delete/ModalAlertDelete';
import jobApi from '~/apis/job';
import { IPayloadDeleteAccount } from '~/utils/interface/account';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import EditFolderDialog from './edit-folder-dialog/EditFolderDialog';
import folderApi from '~/apis/folder';
import { IFolder, IParamGetFileFromFolder, IPayloadDeleteFolder } from '~/utils/interface/folder';
import UploadFileDialog from '~/components/common/upload-file-dialog/UploadFileDialog';
import { ALL_FILES, CREATE_JOB_FILE } from '~/utils/constants/jobs';
import { handleGetTags } from '~/thunks/tag/tagThunk';
import { ITag } from '~/utils/interface/tag';
import EditFileDialog from '~/components/jobs/edit-file-dialog/EditFileDialog';
import { handleGetFilesFromFolder } from '~/thunks/folder/folderThunk';
import { handleGetFile } from '~/thunks/file/fileThunk';
import MenuOption from '~/components/estimate/menu-option/MenuOption';
import { handleGetEstimate, handleRevokeDocument } from '~/thunks/estimate/estimateThunk';
import SendDocumentDialog from '~/components/estimate/esign/send-document/SendDocumentDialog';
import CreateReportFile from './create-report-file/CreateReportFile';
import ModalDownloadFile from './modal-download-file/ModalDownloadFile';
import fileApi from '~/apis/file';

const cx = classNames.bind(styles);
type FileProps = {
  data: IJob | undefined;
  id?: string;
};

const initialValues: IJobFileCreate = {
  files: undefined,
};

const File = (props: FileProps) => {
  const { data, id } = props;

  const dayjs = require('dayjs');
  const { t } = useTranslation('admin/job');
  const dispatch = useAppDispatch();
  const {
    filterFile,
    needRefreshData,
    openAlertDelete,
    openAlertDeleteFolder,
    menuSelectedId,
    menuSelectedFolderId,
    selectedIds,
    filterFolder,
    folders,
    selectedFolderIds,
  } = useAppSelector((state) => state.job);
  const { files, totalPages, currentPage } = useAppSelector((state) => state.folder);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElFolder, setAnchorElFolder] = useState<null | HTMLElement>(null);
  const permissions = usePermission();
  const [openEditFolderDialog, setOpenEditFolderDialog] = useState<boolean>(false);
  const [editFolderDialogMode, setEditFolderDialogMode] = useState<string>('');
  const [editDataFolder, setEditDataFolder] = useState<string>();
  const [selectedFolder, setSelectedFolder] = useState<IFolder>();
  const [openEditFileDialog, setOpenEditFileDialog] = useState<boolean>(false);
  const [editFileData, setEditFileData] = useState<IJobFile>();
  const [openUploadFile, setOpenUploadFile] = useState(false);
  const [enableSign, setEnableSign] = useState<boolean>(false);
  const [enableRevoke, setEnableRevoke] = useState<boolean>(false);
  const [openCreateReportFile, setOpenCreateReportFile] = useState(false);
  const [isOpenModalDownloadFile, setIsOpenModalDownloadFile] = useState(false);
  const [fileDownload, setFileDownload] = useState<IJobFile>();
  const optionUploadFile: OptionUploadFile = {
    uploadFile: true,
    selectFromProject: false,
  };
  const [selectedTags, setSelectedTags] = useState<Array<ITag>>([]);
  const [estimateId, setEstimateId] = useState<number>();
  const [documentFile, setDocumentFile] = useState<IResponseGetFile>();
  const [openSendDocumentDialog, setOpenSendDocumentDialog] = useState<boolean>(false);

  const { tags } = useAppSelector((state) => state.tag);

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: number, file: any) => {
    setAnchorEl(event.currentTarget);
    setEstimateId(file.estimateId);
    if (file.signStatus === 'Signature Needed') setEnableRevoke(true);
    else setEnableRevoke(false);
    if (!file.signStatus) setEnableSign(true);
    else setEnableSign(false);
  };

  const openOptionMenuFolder = (event: React.MouseEvent<HTMLButtonElement>, itemId?: string) => {
    setAnchorElFolder(event.currentTarget);
    dispatch(jobActions.changeMenuSelectFolderId(itemId));
  };

  const handleSign = async () => {
    handleClose();
    if (estimateId) {
      const response = await dispatch(handleGetEstimate(estimateId.toString())).unwrap();
      setDocumentFile(response.documentFile);
      setOpenSendDocumentDialog(true);
    } else {
      if (menuSelectedId) {
        const file = files.filter((file, index) => file.id === menuSelectedId);
        const fileId = file[0].fileId;
        if (fileId) {
          const response = await dispatch(handleGetFile({ id: fileId })).unwrap();
          setDocumentFile(response);
          setOpenSendDocumentDialog(true);
        }
      }
    }
  };

  const handleRevoke = async () => {
    handleClose();
    try {
      if (!menuSelectedId) return;
      const file = files.filter((file, index) => file.id === menuSelectedId);
      const fileId = file[0].fileId;
      if (!fileId) return;

      await dispatch(handleRevokeDocument(fileId)).unwrap();
      dispatch(jobActions.changeNeedRefreshData(true));
      notify('success', t('revokeSuccess', { ns: 'admin/common' }));
    } catch (e) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseModalDownloadFile = () => {
    setIsOpenModalDownloadFile(false);
  };

  const handleCloseFolder = () => {
    setAnchorElFolder(null);
  };

  const handleDeleteItem = () => {
    handleClose();
    dispatch(jobActions.changeOpenAlertDelete(true));
  };

  const handleEditItem = async () => {
    handleClose();
    if (menuSelectedId) {
      const file = files.filter((file, index) => file.id === menuSelectedId);
      const fileId = file[0].fileId;
      if (fileId) {
        const response = await dispatch(handleGetFile({ id: fileId })).unwrap();
        setEditFileData(response);
        setOpenEditFileDialog(true);
      }
    }
  };

  const handleDownloadFile = async (fileSelected: any) => {
    handleClose();
    if (fileSelected) {
      const file = files.filter((file, index) => file.id === fileSelected.id);
      const fileId = file[0].fileId;
      if (fileId) {
        const response = await dispatch(handleGetFile({ id: fileId })).unwrap();
        setFileDownload(response);
        setIsOpenModalDownloadFile(true);
      }
    }
  };

  const handleSetOpenEditFileDialog = async (value: boolean, needRefreshData?: boolean | undefined) => {
    setOpenEditFileDialog(value);
    needRefreshData && (await dispatch(jobActions.changeNeedRefreshData(needRefreshData)));
  };

  const handleDeleteItemFolder = () => {
    handleCloseFolder();
    dispatch(jobActions.changeOpenAlertDeleteFolder(true));
  };

  const handleEditItemFolder = () => {
    handleCloseFolder();
    setEditFolderDialogMode(DIALOG_MODE_EDIT);
    setOpenEditFolderDialog(true);
  };

  const onDeleteSelectedJobs = async () => {
    const payloadDeleteAccounts: IPayloadDeleteAccount =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await jobApi.deleteFiles(data?.id, payloadDeleteAccounts);
      await dispatch(jobActions.changeNeedRefreshData(true));
      await dispatch(jobActions.changeOpenAlertDelete(false));
      notify('success', t('deleteSuccessMessage'));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      const formData = new FormData();
      values.files &&
        values.files.forEach((file) => {
          file.file && formData.append('files', file.file);
        });

      try {
        await dispatch(handleCreateJobFiles({ id: data?.id, data: formData })).unwrap();
        await dispatch(jobActions.changeNeedRefreshData(true));
        notify('success', t('createSuccess', { ns: 'admin/common' }));
      } catch (e: unknown) {
        catchError(e, (message) => {
          notify('error', message);
        });
        handleApiError(e, setFieldError);
      }
    },
  });
  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(
      jobActions.setFilterFile({
        page: newPage,
      })
    );
  };

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(jobActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const closeAlertDeleteModalFolder = useCallback(() => {
    dispatch(jobActions.changeOpenAlertDeleteFolder(false));
  }, [dispatch]);

  const foldersWithAllFiles = useMemo(() => {
    if (!folders) return [];
    return [{ id: '0', name: 'All Files', targetType: 'companies', targetId: '0' }, ...folders];
  }, [folders]);

  useEffect(() => {
    if (foldersWithAllFiles.length > 0 && !selectedFolder) {
      setSelectedFolder(foldersWithAllFiles[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foldersWithAllFiles]);

  useEffect(() => {
    selectedFolder &&
      data?.id &&
      dispatch(
        handleGetFilesFromFolder({
          id: selectedFolder.name === ALL_FILES ? '0' : selectedFolder.id,
          params: { ...filterFile, ...{ type: 'jobFile', typeId: data.id } } as any as IParamGetFileFromFolder,
        })
      )
        .unwrap()
        .catch((e) => {
          handleApiError(e);
        });
  }, [dispatch, data, filterFile, needRefreshData, selectedFolder]);

  useEffect(() => {
    dispatch(handleGetTags()).unwrap();
  }, [dispatch]);

  useEffect(() => {
    data?.id &&
      dispatch(handleGetJobFolders({ id: data?.id, data: filterFolder }))
        .unwrap()
        .catch((e) => {
          handleApiError(e);
        });
  }, [dispatch, data, filterFolder, needRefreshData]);

  useEffect(() => {
    if (form.values.files?.length) {
      form.handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.files]);

  const handleCreateFolder = async () => {
    setEditFolderDialogMode(DIALOG_MODE_CREATE);
    setOpenEditFolderDialog(true);
  };

  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    setOpenEditFolderDialog(open);
    setEditFolderDialogMode('');
    if (needRefreshData) dispatch(jobActions.changeNeedRefreshData(true));
  };

  const onDeleteSelectedFolders = async () => {
    const payloadDeleteFolders: IPayloadDeleteFolder =
      menuSelectedFolderId !== undefined ? { ids: [menuSelectedFolderId] } : { ids: [...selectedFolderIds] };
    try {
      await folderApi.deleteFolder(payloadDeleteFolders);
      await dispatch(jobActions.changeNeedRefreshData(true));
      await dispatch(jobActions.changeOpenAlertDeleteFolder(false));
      notify('success', t('deleteSuccessMessage'));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  };

  const handleSelectedTags = (values: ITag[]) => {
    setSelectedTags(values);
    dispatch(
      jobActions.setFilterFile({
        page: 1,
        selectedTags: values.map((index, key) => index.id),
      })
    );
  };

  const handleDownload = async () => {
    handleClose();
    if (menuSelectedId) {
      const file = files.filter((file, index) => file.id === menuSelectedId);
      const fileId = file[0].fileId;
      if (!fileId) {
        return;
      }
      const result = await fileApi.downloadFileUrl(fileId);
      const fileUrl = result?.data?.data?.fileUrl;
      if (result?.data?.data?.fileUrl) {
        const anchor = document.createElement('a');
        anchor.href = String(fileUrl);
        anchor.download = result?.data?.data?.name ?? '';
        anchor.click();
      }
    }
  };

  return (
    <BaseRow className='gap-[30px]'>
      <Box className='lg:w-[30%]'>
        <Box className='w-full text-end mb-[10px]'>
          <Button
            className='h-[36px] px-[12px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-lg w-full'
            variant='outlined'
            size='large'
            startIcon={<UploadFileIcon className='w-[12px] h-[12px]' />}
            onClick={() => setOpenUploadFile(true)}
          >
            {t('uploadFile', { ns: 'admin/common' })}
          </Button>
        </Box>
        <Box className='w-full text-end mb-[10px]'>
          <Button
            className='h-[36px] px-[12px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-lg w-full'
            variant='outlined'
            size='large'
            startIcon={<DocumentActiveIcon className='w-[12px] h-[12px]' />}
            onClick={() => setOpenCreateReportFile(true)}
          >
            {t('files.createReportFile')}
          </Button>
        </Box>
        <Box className='flex flex-row mb-[30px]'>
          <BaseHeaderTitle title={t('titleFolder')} />
          <Box>
            {permissions[PERMISSIONS.projectFile_create] && (
              <Button
                className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-lg ml-[10px]'
                variant='contained'
                disableElevation
                startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
                onClick={handleCreateFolder}
              >
                {t('addFolder')}
              </Button>
            )}
          </Box>
        </Box>
        <Box className={cx('folder-container')}>
          {permissions[PERMISSIONS.projectFile_view] &&
            foldersWithAllFiles?.map((folder, index) => (
              <Box className={cx('folderContent')} key={index}>
                <Box
                  className={cx(
                    'folderItem',
                    {
                      active: selectedFolder && folder.id === selectedFolder.id,
                    },
                    ' hover:bg-stone-100 cursor-pointer'
                  )}
                  onClick={() => {
                    dispatch(jobActions.setFilterFile({ page: 1 }));
                    setSelectedFolder(folder);
                  }}
                >
                  <FolderIcon className='w-[20px] h-[20px] folderIcon' />
                  <Typography className={cx('folderName')}>{folder?.name}</Typography>
                  {folder?.targetType !== 'companies' && (
                    <Box className={cx('folderAction')}>
                      <IconButton
                        onClick={(event) => {
                          openOptionMenuFolder(event, folder.id);
                          dispatch(jobActions.changeMenuSelectFolderId(folder?.id));
                          setEditDataFolder(folder.name);
                        }}
                      >
                        <MenuVerticalIcon className='w-[18px] h-[18px]' />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Divider className={cx('lineFolder')} />
              </Box>
            ))}
        </Box>
        <Box>
          <Box className='pb-[15px]'>
            <Typography className='my-[15px]'>{t('estimate.fileItem.tags')}</Typography>
            <Autocomplete
              multiple
              disableClearable
              value={selectedTags || []}
              onChange={(event, newValue) => handleSelectedTags(newValue)}
              options={tags}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              popupIcon={null}
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant='outlined'
                  placeholder={t('searchPlaceholder')}
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      paddingTop: 0,
                      paddingBottom: 0,
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
      <Box className='lg:w-[70%]'>
        <Box className={cx('file-container')}>
          <Typography variant='h6' gutterBottom className={cx('title')}>
            {selectedFolder?.name}
          </Typography>

          <Box className={cx('box-file-container')}>
            <Box className={cx('box-file')}>
              {files?.map((file, index) => (
                <Box key={index} className={cx('file')}>
                  {file.signStatus === 'Signed' && <Box className={cx('signed')}> {file.signStatus}</Box>}
                  {file.signStatus === 'Signature Needed' && (
                    <Box className={cx('signatureNeeded')}> {file.signStatus}</Box>
                  )}
                  <IconButton
                    className={cx('menuIcon')}
                    onClick={(event) => {
                      dispatch(jobActions.changeMenuSelectId(file?.id));
                      openOptionMenu(event, index, file);
                    }}
                  >
                    <MenuVerticalIcon className='w-[12px] h-[12px]' />
                  </IconButton>
                  <Box onClick={() => handleDownloadFile(file)} className={cx('file-content')}>
                    {file?.type === 'IMAGE' ? (
                      <BaseImage src={file?.url} className={cx('image')} />
                    ) : (
                      <FileIcon className={cx('fileIcon')} />
                    )}

                    <Box>
                      <Typography className={cx('fileName')}>{file?.name}</Typography>
                    </Box>
                    <Divider className={cx('line')} />
                    <Box className={cx('create')}>
                      <Box className={cx('box-create')}>
                        <Typography variant='caption' className={cx('createBy')}>
                          {t('files.createdBy')}
                        </Typography>
                        <Typography variant='caption' className={cx('createByText')}>
                          {file.createdByAccount}
                        </Typography>
                      </Box>

                      <Box className={cx('box-create-at')}>
                        <Typography variant='caption' className={cx('createBy')}>
                          {t('files.createdAt')}
                        </Typography>
                        <Typography variant='caption' className={cx('createByText')}>
                          {dayjs(file.createdAt).format('MM/DD/YYYY')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box className={cx('pagination')}>
              <BasePagination count={totalPages} page={currentPage} onChange={handleChangePage} />
            </Box>
            <ModalAlertDelete
              open={openAlertDelete}
              description={t('alertDescriptionFile')}
              onCancel={closeAlertDeleteModal}
              onConfirm={onDeleteSelectedJobs}
            />
            <EditFileDialog
              open={openEditFileDialog}
              id={props.id}
              data={editFileData}
              folders={folders}
              setOpen={handleSetOpenEditFileDialog}
            />
          </Box>
        </Box>
      </Box>

      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        handleSign={handleSign}
        enableRevoke={enableRevoke}
        handleRevoke={handleRevoke}
        enableSign={permissions[PERMISSIONS.projectEstimate_esign] && enableSign}
        enableDelete={permissions[PERMISSIONS.projectFile_delete]}
        enableEdit={permissions[PERMISSIONS.projectFile_edit]}
        handleDownload={handleDownload}
      />

      <ModalDownloadFile open={isOpenModalDownloadFile} file={fileDownload} onClose={handleCloseModalDownloadFile} />
      <MenuOptionFolder
        anchorEl={anchorElFolder}
        open={Boolean(anchorElFolder)}
        onClose={handleCloseFolder}
        handleEditItem={handleEditItemFolder}
        handleDeleteItem={handleDeleteItemFolder}
        enableDelete={permissions[PERMISSIONS.projectFile_delete]}
        enableEdit={permissions[PERMISSIONS.projectFile_edit]}
      />

      <EditFolderDialog
        open={openEditFolderDialog}
        mode={editFolderDialogMode}
        setOpen={handleSetOpen}
        data={editDataFolder}
        id={id}
      />

      <CreateReportFile
        open={openCreateReportFile}
        data={data}
        setOpen={(open) => {
          setOpenCreateReportFile(open);
        }}
      />

      <ModalAlertDeleteFolder
        open={openAlertDeleteFolder}
        description={t('alertDescriptionFolder')}
        onCancel={closeAlertDeleteModalFolder}
        onConfirm={onDeleteSelectedFolders}
      />

      <UploadFileDialog
        jobId={props.id}
        open={openUploadFile}
        setOpen={(value: boolean) => setOpenUploadFile(value)}
        optionUploadFile={optionUploadFile}
        type={CREATE_JOB_FILE}
        uploadedToFolder={(value) => {
          const folder = folders.filter((folder, index) => folder.id === value);
          folder && setSelectedFolder(folder[0]);
          dispatch(jobActions.changeNeedRefreshData(true));
        }}
        data={filterFolder}
        shouldQueue={true}
        job={data}
      />

      <SendDocumentDialog
        open={openSendDocumentDialog}
        id={estimateId}
        documentFile={documentFile}
        setOpen={(value, success) => {
          setOpenSendDocumentDialog(value);
          success && dispatch(jobActions.changeNeedRefreshData(success));
        }}
        jobId={data?.id}
        customerEmail={data?.customer?.email}
      />
    </BaseRow>
  );
};

export default File;
